import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  DataTableFilterMeta,
  DataTableRowToggleEvent,
} from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { ListBox } from "primereact/listbox";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IcmJobProgress,
  IcmStage,
  ReportJobBatchEntry,
  ReportJobEntry,
  WorkStatus,
} from "../../../../client";
import { LOB } from "../../../../api/routes";
import { useQueueJobs } from "../../rater/useIntegrationJobs";
import { ROUTES } from "../../../../routes";
import {
  useCancelJobs,
  useLocalUser,
  useSafeReports,
  useSetBatchPriority,
  useUsers,
} from "../../data";
import { isRunningLocally } from "../../../../util";
import styles from "./shared.module.css";
import {
  SETTABLE_PRIORITIES,
  SettablePriority,
  formatTimestamp,
  priorityLabel,
} from "./submissionHistoryUtils";

type StatusLabel =
  | "In Progress"
  | "Pending"
  | "Partially Running"
  | "Retrying"
  | "Blocked"
  | "Waiting to submit to ICM";

const ICM_STAGE_LABELS: Record<IcmStage, string> = {
  submitted: "submitted",
  cat_modeling: "cat modeling",
  scoring: "scoring",
  complete: "complete",
};

type TopicBreakdown = {
  topic: string;
  done: number;
  total: number;
};

type QueueRow = ReportJobBatchEntry & {
  company_name: string;
  triggered_by: string;
  min_position: number | null;
  position_display: string;
  status_label: StatusLabel;
  topic_breakdown: TopicBreakdown[];
  icm_progress: IcmJobProgress | null;
};

const TERMINAL_STATUSES: ReadonlySet<WorkStatus> = new Set<WorkStatus>([
  "done",
  "failed",
  "cancelled",
]);

const isTerminal = (status: WorkStatus) => TERMINAL_STATUSES.has(status);

// A job asleep before a retry is PENDING but was already picked up, so it
// counts as in flight, not queued.
const isInFlight = (job: ReportJobEntry) =>
  job.status === "in_progress" || job.sleeping_until != null;

const STATUS_OPTIONS = [
  "In Progress",
  "Pending",
  "Partially Running",
  "Retrying",
  "Blocked",
  "Waiting to submit to ICM",
];

const PRODUCTION_ENV = "production";

// Service users have no app_user row, so /users never names them. Their ids are
// `<prefix>_<client_id>` (_client_user in backend/pantheon/src/database/user.py).
const SERVICE_USER_LABELS: Record<string, string> = {
  automation_for: "Automation",
  email_ingest_for: "Email ingest",
  script_run_on_behalf_of: "Script",
  super_user_for: "Super user",
};

const triggeredByLabel = (
  userId: string | null,
  userNameById: Record<string, string | undefined>
): string => {
  if (userId == null) return "—";
  const name = userNameById[userId];
  if (name != null) return name;
  const service = Object.entries(SERVICE_USER_LABELS).find(([prefix]) =>
    userId.startsWith(`${prefix}_`)
  );
  return service?.[1] ?? userId;
};

const defaultFilters: DataTableFilterMeta = {
  integration_type: { value: null, matchMode: FilterMatchMode.EQUALS },
  status_label: { value: null, matchMode: FilterMatchMode.EQUALS },
  env: {
    value: isRunningLocally() ? null : PRODUCTION_ENV,
    matchMode: FilterMatchMode.EQUALS,
  },
};

const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={STATUS_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Statuses"
    showClear
    style={{ minWidth: "150px" }}
  />
);

const statusSeverity = (
  status: WorkStatus
): "success" | "info" | "warning" | "danger" => {
  switch (status) {
    case "done":
      return "success";
    case "in_progress":
      return "info";
    case "pending":
      return "warning";
    case "failed":
    case "cancelled":
      return "danger";
  }
};

const NORMAL_PRIORITY: SettablePriority = 0;

// Only a deliberate enqueuer or an admin moves a job off normal, so only those
// rows get a tag.
const PriorityDisplay = ({ priority }: { priority: number }) => {
  if (priority === NORMAL_PRIORITY) return <span>Normal</span>;
  return (
    <Tag
      value={priorityLabel(priority)}
      severity={priority > NORMAL_PRIORITY ? "warning" : "info"}
    />
  );
};

const PRIORITY_OPTIONS = SETTABLE_PRIORITIES.map((value) => ({
  value,
  label: priorityLabel(value),
}));

const BatchPriorityControl = ({
  batchId,
  priority,
}: {
  batchId: string;
  priority: number;
}) => {
  const overlayPanel = useRef<OverlayPanel>(null);
  const setBatchPriority = useSetBatchPriority();
  return (
    <>
      <Button
        text
        size="small"
        className={styles.priorityButton}
        aria-label="Change priority"
        loading={setBatchPriority.isPending}
        onClick={(e) => overlayPanel.current?.toggle(e)}
      >
        <PriorityDisplay priority={priority} />
        <i className={`pi pi-pencil ${styles.priorityEditIcon}`} />
      </Button>
      <OverlayPanel ref={overlayPanel} dismissable>
        <ListBox
          value={priority}
          options={PRIORITY_OPTIONS}
          disabled={setBatchPriority.isPending}
          onChange={(e) => {
            const value = e.value as SettablePriority | null;
            // ListBox emits null when the current option is clicked again.
            if (value == null || value === priority) return;
            setBatchPriority.mutate(
              { batch_id: batchId, priority: value },
              { onSuccess: () => overlayPanel.current?.hide() }
            );
          }}
        />
        {setBatchPriority.isError && (
          <small className={styles.priorityError}>
            Couldn&apos;t change priority
          </small>
        )}
      </OverlayPanel>
    </>
  );
};

// The API returns each batch's jobs in pipeline order (see
// task_dependencies.TOPIC_PIPELINE on the backend), so iterating in
// the received order — Map preserves insertion order — gives us the
// breakdown rows in the right sequence with a single pass.
const computeTopicBreakdown = (jobs: ReportJobEntry[]): TopicBreakdown[] => {
  const counts = new Map<string, { done: number; total: number }>();
  for (const job of jobs) {
    const cur = counts.get(job.topic) ?? { done: 0, total: 0 };
    cur.total += 1;
    if (isTerminal(job.status)) cur.done += 1;
    counts.set(job.topic, cur);
  }
  return [...counts.entries()].map(([topic, c]) => ({
    topic,
    done: c.done,
    total: c.total,
  }));
};

const TopicBreakdownDisplay = ({ rows }: { rows: TopicBreakdown[] }) => {
  if (rows.length === 0) return null;
  return (
    <div className={styles.topicBreakdown}>
      {rows.map((r) => {
        const fullyDone = r.done === r.total;
        return (
          <div
            key={r.topic}
            className={[
              styles.topicLine,
              fullyDone ? styles.topicLineDone : styles.topicLineActive,
            ].join(" ")}
          >
            <span>{r.topic}</span>
            <span>
              {r.done}/{r.total}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ResponseIcon = ({
  response,
}: {
  response: ReportJobEntry["response"];
}) => {
  const overlayPanel = useRef<OverlayPanel>(null);
  if (response == null) return <span>—</span>;
  return (
    <>
      <Button
        icon="pi pi-file"
        text
        rounded
        size="small"
        className={styles.responseButton}
        aria-label="View response"
        onClick={(e) => overlayPanel.current?.toggle(e)}
      />
      <OverlayPanel ref={overlayPanel} showCloseIcon dismissable>
        <pre
          style={{
            margin: 0,
            maxWidth: "600px",
            maxHeight: "400px",
            overflow: "auto",
            fontSize: "0.75rem",
          }}
        >
          {JSON.stringify(response, null, 2)}
        </pre>
      </OverlayPanel>
    </>
  );
};

export const JobsInQueue = () => {
  const user = useLocalUser();
  const lobsForUser = (user?.lobs ?? []) as LOB[];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  const reports = useSafeReports(lobsForUser).data ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  const allUsers = useUsers().data?.all_company_users ?? [];
  const cancelJobs = useCancelJobs();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const reportIds = useMemo(() => reports.map((r) => r.report_id), [reports]);

  const companyNameByReportId = useMemo(
    () =>
      Object.fromEntries(
        reports.map((r) => [
          r.report_id,
          r.report_metadata.company_info.company_name,
        ])
      ),
    [reports]
  );

  const userNameById = useMemo(
    () =>
      Object.fromEntries(
        allUsers.map((u) => [u.id, u.full_name?.trim() || u.email])
      ),
    [allUsers]
  );

  const { data: jobs = [], isLoading } = useQueueJobs(reportIds, [
    "pending",
    "in_progress",
  ]);

  const rows: QueueRow[] = useMemo(() => {
    return jobs.map((job) => {
      const activeJobs = job.jobs.filter(
        (j) => j.status === "pending" || j.status === "in_progress"
      );
      const pendingPositions = activeJobs
        .map((j) => j.queue_position)
        .filter((p): p is number => p != null);
      const inFlightCount = activeJobs.filter(isInFlight).length;
      const runningCount = activeJobs.filter(
        (j) => j.status === "in_progress"
      ).length;
      // ISO timestamps sort chronologically as strings.
      const earliestRetry =
        activeJobs
          .map((j) => j.sleeping_until)
          .filter((t): t is string => t != null)
          .sort()[0] ?? null;
      const isRetrying =
        pendingPositions.length === 0 &&
        runningCount === 0 &&
        earliestRetry != null;

      const min =
        pendingPositions.length > 0 ? Math.min(...pendingPositions) : null;
      const max =
        pendingPositions.length > 0 ? Math.max(...pendingPositions) : null;

      const position_display: string =
        isRetrying && earliestRetry != null
          ? `retries ${formatTimestamp(earliestRetry)}`
          : pendingPositions.length === 0
            ? "—"
            : min === max
              ? `${min}`
              : `${min}–${max}`;

      const isBlocked =
        pendingPositions.length > 0 && (job.blocked_by_kinds ?? []).length > 0;

      const icmExportJob = activeJobs.find((j) => j.icm_progress != null);
      const icmProgress = icmExportJob?.icm_progress ?? null;
      const icmWaitingForSlot =
        icmExportJob != null &&
        isInFlight(icmExportJob) &&
        icmProgress != null &&
        !icmProgress.submitted;

      const status_label: StatusLabel = icmWaitingForSlot
        ? "Waiting to submit to ICM"
        : inFlightCount > 0 && pendingPositions.length > 0
          ? "Partially Running"
          : isRetrying
            ? "Retrying"
            : pendingPositions.length === 0
              ? "In Progress"
              : isBlocked
                ? "Blocked"
                : "Pending";

      const triggered_by = triggeredByLabel(
        job.initiating_user_id,
        userNameById
      );

      return {
        ...job,
        company_name: companyNameByReportId[job.report_id] ?? "",
        triggered_by,
        min_position: min,
        position_display,
        status_label,
        topic_breakdown: computeTopicBreakdown(job.jobs),
        icm_progress: icmProgress,
      };
    });
  }, [jobs, companyNameByReportId, userNameById]);

  const envOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.env))).sort(),
    [rows]
  );

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.integration_type))).sort(),
    [rows]
  );

  const envFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
      value={options.value}
      options={envOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="All Envs"
      showClear
      style={{ minWidth: "150px" }}
    />
  );

  const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
      value={options.value}
      options={typeOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="All Types"
      showClear
      style={{ minWidth: "150px" }}
    />
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // in_progress (null position) before pending (has position)
        if (a.min_position == null && b.min_position != null) return -1;
        if (a.min_position != null && b.min_position == null) return 1;
        if (a.min_position != null && b.min_position != null)
          return a.min_position - b.min_position;
        // both null (in_progress) — sort by started_at
        return a.started_at.localeCompare(b.started_at);
      }),
    [rows]
  );

  // Compact DataTable expansion. The visual density is driven by CSS
  // overrides scoped under .expansionTable (see shared.module.css) —
  // smaller font, tighter cell padding, lighter header weight — so it
  // reads as a sub-listing rather than another full DataTable.
  const rowExpansionTemplate = (row: QueueRow) => (
    <div className={`${styles.expansionWrap} ${styles.expansionTable}`}>
      <DataTable value={row.jobs} dataKey="report_integration_id" size="small">
        <Column field="topic" header="Topic" />
        <Column
          header="Status"
          body={(job: ReportJobEntry) =>
            job.sleeping_until != null ? (
              <Tag value="retrying" severity="info" />
            ) : (
              <Tag value={job.status} severity={statusSeverity(job.status)} />
            )
          }
        />
        <Column
          header="ICM"
          body={(job: ReportJobEntry) => {
            const p = job.icm_progress;
            if (p == null) return "—";
            if (!p.submitted) return "waiting to submit";
            const stage =
              p.stage != null ? ICM_STAGE_LABELS[p.stage] : "submitted";
            return p.location_count != null
              ? `${stage} · ${p.location_count.toLocaleString()} locations`
              : stage;
          }}
        />
        <Column
          header="Position"
          body={(job: ReportJobEntry) =>
            job.queue_position != null
              ? `#${job.queue_position}`
              : job.sleeping_until != null
                ? `retries ${formatTimestamp(job.sleeping_until)}`
                : "—"
          }
        />
        <Column
          header="Priority"
          body={(job: ReportJobEntry) => (
            <PriorityDisplay priority={job.priority} />
          )}
        />
        <Column
          header="Started"
          body={(job: ReportJobEntry) => formatTimestamp(job.started_at)}
        />
        <Column
          header="Response"
          body={(job: ReportJobEntry) => (
            <ResponseIcon response={job.response} />
          )}
        />
        <Column
          header=""
          body={(job: ReportJobEntry) => {
            const isCancellingThis =
              cancelJobs.isPending &&
              Array.isArray(cancelJobs.variables) &&
              cancelJobs.variables.includes(job.report_integration_id);
            return (
              <Button
                label="Cancel"
                severity="warning"
                size="small"
                text
                className={styles.jobCancelButton}
                disabled={isTerminal(job.status) || cancelJobs.isPending}
                loading={isCancellingThis}
                onClick={() => cancelJobs.mutate([job.report_integration_id])}
              />
            );
          }}
        />
      </DataTable>
    </div>
  );

  return (
    <div className={styles.page}>
      <Card title="Jobs in Queue">
        {isLoading ? (
          <div className={styles.centered}>
            <ProgressSpinner />
          </div>
        ) : (
          <div className={styles.queueTable}>
            <DataTable
              value={sortedRows}
              filters={filters}
              filterDisplay="menu"
              onFilter={(e) => setFilters(e.filters)}
              emptyMessage="No jobs in queue"
              scrollable
              scrollHeight="calc(100vh - 300px)"
              stripedRows
              dataKey="batch_id"
              expandedRows={expandedRows}
              onRowToggle={(e: DataTableRowToggleEvent) =>
                setExpandedRows(e.data as Record<string, boolean>)
              }
              rowExpansionTemplate={rowExpansionTemplate}
              rowClassName={(row: QueueRow) =>
                expandedRows[row.batch_id] ? styles.expandedParent : ""
              }
            >
              <Column expander style={{ width: "3rem" }} />
              <Column
                field="env"
                header="Env"
                filter
                filterElement={envFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "160px" }}
              />
              <Column
                field="min_position"
                header="Position"
                sortable
                style={{ width: "100px" }}
                body={(row: QueueRow) => row.position_display}
              />
              <Column
                field="priority"
                header="Priority"
                sortable
                style={{ width: "130px" }}
                body={(row: QueueRow) =>
                  // The backend only reprioritizes a batch with a pending job.
                  row.jobs.some((j) => j.status === "pending") ? (
                    <BatchPriorityControl
                      batchId={row.batch_id}
                      priority={row.priority}
                    />
                  ) : (
                    <PriorityDisplay priority={row.priority} />
                  )
                }
              />
              <Column
                field="status_label"
                header="Status"
                filter
                filterElement={statusFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "240px" }}
                body={(row: QueueRow) => (
                  <div className={styles.statusCell}>
                    <Tag
                      value={row.status_label}
                      severity={
                        row.status_label === "Pending" ||
                        row.status_label === "Partially Running" ||
                        row.status_label === "Waiting to submit to ICM"
                          ? "warning"
                          : row.status_label === "Blocked"
                            ? "danger"
                            : "info"
                      }
                    />
                    {row.status_label === "Blocked" &&
                      row.blocked_by_kinds.length > 0 && (
                        <span className={styles.stageDisplay}>
                          waiting on {row.blocked_by_kinds.join(", ")}
                        </span>
                      )}
                    {row.status_label === "Waiting to submit to ICM" && (
                      <span className={styles.stageDisplay}>
                        queued behind active ICM rating
                      </span>
                    )}
                    {row.icm_progress?.submitted &&
                      row.icm_progress.stage != null && (
                        <span className={styles.stageDisplay}>
                          ICM: {ICM_STAGE_LABELS[row.icm_progress.stage]}
                          {row.icm_progress.location_count != null &&
                            ` · ${row.icm_progress.location_count.toLocaleString()} locations`}
                        </span>
                      )}
                    <TopicBreakdownDisplay rows={row.topic_breakdown} />
                  </div>
                )}
              />
              <Column
                field="integration_type"
                header="Type"
                filter
                filterElement={typeFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "200px" }}
              />
              <Column
                field="company_name"
                header="Company"
                sortable
                style={{ minWidth: "200px" }}
                body={(row: QueueRow) => (
                  <Link to={ROUTES.DASHBOARD_REPORT(row.report_id)}>
                    {row.company_name}
                  </Link>
                )}
              />
              <Column
                field="triggered_by"
                header="Triggered By"
                sortable
                style={{ width: "200px" }}
              />
              <Column
                field="started_at"
                header="Started"
                sortable
                style={{ width: "200px" }}
                body={(row: QueueRow) => formatTimestamp(row.started_at)}
              />
              <Column
                header="Jobs"
                style={{ width: "80px" }}
                body={(row: QueueRow) => row.jobs.length}
              />
              <Column
                header=""
                style={{ width: "120px" }}
                alignFrozen="right"
                frozen
                body={(row: QueueRow) => {
                  const activeIds = row.jobs
                    .filter(
                      (j) =>
                        j.status === "pending" || j.status === "in_progress"
                    )
                    .map((j) => j.report_integration_id);
                  const isCancellingThis =
                    cancelJobs.isPending &&
                    Array.isArray(cancelJobs.variables) &&
                    cancelJobs.variables.some((id) => activeIds.includes(id));
                  return (
                    <Button
                      label="Cancel"
                      severity="warning"
                      size="small"
                      disabled={activeIds.length === 0 || cancelJobs.isPending}
                      loading={isCancellingThis}
                      onClick={() => cancelJobs.mutate(activeIds)}
                    />
                  );
                }}
              />
            </DataTable>
          </div>
        )}
      </Card>
    </div>
  );
};
